export const USER_ROLES = {
  ADMIN: 'admin',
  INDIVIDUAL_CONTRACTOR: 'individual_contractor',
  COMPANY_CONTRACTOR: 'company_contractor',
  CONTRACTOR_OF_RECORD: 'contractor_of_record',
  GIG_WORKER: 'gig_worker',
  EMPLOYEE: 'employee',
  EMPLOYER: 'employer',
  EMPLOYMENT_MANAGER: 'employment_manager',
  FINANCE: 'finance',
  INVOICE_MANAGER: 'invoice_manager',
  INVOICE_USER: 'invoice_user',
  LEGAL: 'legal',
  ONBOARDING_MANAGER: 'onboarding_manager',
  RELATIONSHIP_MANAGER: 'relationship_manager',
  KEY_ACCOUNT_MANAGER: 'key_account_manager',
  ORGANISATION_OWNER: 'organisation_owner',
  READER: 'reader',
  CX_MANAGER: 'cx_manager',
  PAYROLL_SEAT: 'payroll_seat',
  DRAFT_SEAT: 'draft_seat',
  TEAMS_GLOBAL_ADMIN: 'teams_global_admin',
  GLOBAL_ADMIN: 'teams_global_admin',
  SALES_MANAGER: 'sales_manager',
  PAYROLL_MANAGER: 'payroll_manager',
  B2C_STANDARD: 'b2c_standard', // Not employed yet, still in onboarding process
  B2C_EMPLOYEE: 'b2c_employee', // Employed
  B2C_FREE: 'b2c_free', // Free / inactive plan
  CLC_MANAGER: 'clc_manager',
  TAX_MANAGER: 'tax_manager',
};

export const CONTRACTOR_ROLES = {
  INDIVIDUAL_CONTRACTOR: USER_ROLES.INDIVIDUAL_CONTRACTOR,
  COMPANY_CONTRACTOR: USER_ROLES.COMPANY_CONTRACTOR,
  CONTRACTOR_OF_RECORD: USER_ROLES.CONTRACTOR_OF_RECORD,
} as const;

export type ContractorRole = (typeof CONTRACTOR_ROLES)[keyof typeof CONTRACTOR_ROLES];

export const CONTRACTOR_ROLE_VALUES: ContractorRole[] = Object.values(CONTRACTOR_ROLES);

export const B2C_ROLES = {
  B2C_STANDARD: USER_ROLES.B2C_STANDARD,
  B2C_EMPLOYEE: USER_ROLES.B2C_EMPLOYEE,
  B2C_FREE: USER_ROLES.B2C_FREE,
} as const;

export const PERMISSIONS = {
  // Create permissions
  CREATE_ANY_CONTRACTORS: 'create any contractors',
  CREATE_ANY_EMPLOYEES: 'create any employees',
  CREATE_ANY_EMPLOYMENT_DETAILS: 'create any employment_details',
  CREATE_ANY_MSA_DETAILS: 'create any msa_details',
  CREATE_ANY_ORGANISATIONS: 'create any organisations',
  CREATE_ANY_USERS: 'create any users',
  CREATE_OWN_CONTRACTORS: 'create own contractors',
  CREATE_OWN_EMPLOYEES: 'create own employees',
  CREATE_OWN_EMPLOYMENT_DETAILS: 'create own employment_details',
  CREATE_OWN_MSA_DETAILS: 'create own msa_details',
  CREATE_OWN_USERS: 'create own users',
  CREATE_ANY_ADD_DEDUCT_FUNDS: 'create any add_deduct_funds',
  CREATE_ANY_ADMIN_BATCH_DEDUCTIONS: 'create any admin_batch_deductions',
  CREATE_ANY_ALERTS: 'create any alerts',
  CREATE_ANY_ANNUAL_REPORT_DOCUMENTS: 'create any annual_report documents',
  CREATE_ANY_ANNEXE_DOCUMENTS: 'create any annexe documents',
  CREATE_ANY_B2C_USERS: 'create any b2c_users',
  CREATE_ANY_BANK_DETAILS: 'create any bankDetails',
  CREATE_ANY_CARD_ORDER_REQUESTS: 'create any card_order_requests',
  CREATE_ANY_CARD_REQUESTS: 'create any card_requests',
  CREATE_ANY_CARD_TOP_UP_REQUESTS: 'create any card_top_up_requests',
  CREATE_ANY_CARDS: 'create any cards',
  CREATE_ANY_CONTACTS: 'create any contacts',
  CREATE_ANY_CONTRACT_DOCUMENTS: 'create any contract documents',
  CREATE_ANY_CONTRACTOR_PAYMENTS: 'create any contractor_payments',
  CREATE_ANY_COUNTRY_ANNUAL_LEAVES: 'create any country_annual_leaves',
  CREATE_ANY_COUNTRY_EMPLOYMENT_INFO: 'create any country_employment_info',
  CREATE_ANY_ENTITIES: 'create any entities',
  CREATE_ANY_EXPENSES: 'create any expenses',
  CREATE_ANY_EXPENSE_GUIDELINES: 'create any expense_guidelines',
  CREATE_ANY_GIG_FEE: 'create any gig_fee',
  CREATE_ANY_GIG_PAYMENTS_REQUESTS: 'create any gig_payments_requests',
  CREATE_ANY_GIG_WORKERS: 'create any gig_workers',
  CREATE_ANY_IBAN_REQUESTS: 'create any IBAN_requests',
  CREATE_ANY_INVOICE_DOCUMENTS: 'create any invoice documents',
  CREATE_ANY_INVOICE_REQUESTS: 'create any invoice_requests',
  CREATE_ANY_INVOICES: 'create any invoices',
  CREATE_ANY_MSA_DOCUMENTS: 'create any msa documents',
  CREATE_ANY_ORGANISATION_FEES: 'create any organisation_fees',
  CREATE_ANY_OTHER_DOCUMENTS: 'create any other documents',
  CREATE_ANY_PAYDAYS: 'create any paydays',
  CREATE_ANY_PAYROLL_DOCUMENTS: 'create any payroll documents',
  CREATE_ANY_PAYROLLS: 'create any payrolls',
  CREATE_ANY_PAYROLL_FINAL: 'create any payroll_final',
  CREATE_ANY_PAYSLIP_DOCUMENTS: 'create any payslip documents',
  CREATE_ANY_PAYSLIP_EOR_DOCUMENTS: 'create any payslip eor documents',
  CREATE_ANY_PAYSLIP_PAYROLL_SEAT_DOCUMENTS: 'create any payslip payroll_seat documents',
  CREATE_ANY_PAYSLIP_B2C_DOCUMENTS: 'create any payslip b2c documents',
  CREATE_ANY_PAYSLIP_CONTRACTOR_DOCUMENTS: 'create any payslip contractor documents',
  CREATE_ANY_PAYSLIPS: 'create any payslips',
  CREATE_ANY_PREPAYMENTS: 'create any prepayments',
  CREATE_ANY_PRICING_ADDENDUM_DOCUMENTS: 'create any pricing_addendum documents',
  CREATE_ANY_PUBLIC_HOLIDAYS: 'create any public_holidays',
  CREATE_ANY_REIMBURSEMENT_GUIDELINES: 'create any reimbursement_guidelines',
  CREATE_ANY_SUBSCRIPTION: 'create any subscription',
  CREATE_ANY_TASKS: 'create any tasks',
  CREATE_ANY_TEAM_MEMBERS: 'create any teamMembers',
  CREATE_ANY_TEAMS: 'create any teams',
  CREATE_ANY_TERMINATION: 'create any termination',
  CREATE_ANY_TRANSACTIONS: 'create any transactions',
  CREATE_ANY_USER_FEES: 'create any user_fees',
  CREATE_ANY_USER_VERIFICATION_REQUESTS: 'create any user_verification_requests',
  CREATE_ANY_WALLET_TOP_UP_REQUESTS: 'create any wallet_top_up_requests',
  CREATE_ANY_WALLETS: 'create any wallets',
  CREATE_ANY_WITHDRAWALS: 'create any withdrawals',
  CREATE_IN_OWN_CONTRACTORS: 'create in own contractors',
  CREATE_IN_OWN_EMPLOYEES: 'create in own employees',
  CREATE_IN_OWN_EMPLOYMENT_DETAILS: 'create in own employment_details',
  CREATE_IN_OWN_ANNUAL_REPORT_DOCUMENTS: 'create in own annual_report documents',
  CREATE_IN_OWN_ENTITIES: 'create in own entities',
  CREATE_IN_OWN_EXPENSES: 'create in own expenses',
  CREATE_IN_OWN_GIG_WORKERS: 'create in own gig_workers',
  CREATE_IN_OWN_INVOICES: 'create in own invoices',
  CREATE_IN_OWN_MSA_DETAILS: 'create in own msa_details',
  CREATE_IN_OWN_PAYROLLS: 'create in own payrolls',
  CREATE_IN_OWN_TEAM_MEMBERS: 'create in own teamMembers',
  CREATE_IN_OWN_TEAMS: 'create in own teams',
  CREATE_IN_OWN_USERS: 'create in own users',
  CREATE_OWN_ALERTS: 'create own alerts',
  CREATE_OWN_ANNUAL_REPORT_DOCUMENTS: 'create own annual_report documents',
  CREATE_OWN_ANNEXE_DOCUMENTS: 'create own annexe documents',
  CREATE_OWN_BANK_DETAILS: 'create own bankDetails',
  CREATE_OWN_CARD_ORDER_REQUESTS: 'create own card_order_requests',
  CREATE_OWN_CARD_TOP_UP_REQUESTS: 'create own card_top_up_requests',
  CREATE_OWN_CARDS: 'create own cards',
  CREATE_OWN_CONTACTS: 'create own contacts',
  CREATE_OWN_CONTRACT_DOCUMENTS: 'create own contract documents',
  CREATE_OWN_CONTRACTOR_PAYMENTS: 'create own contractor_payments',
  CREATE_OWN_ENTITIES: 'create own entities',
  CREATE_OWN_EXPENSES: 'create own expenses',
  CREATE_OWN_GIG_FEE: 'create own gig_fee',
  CREATE_OWN_GIG_PAYMENTS_REQUESTS: 'create own gig_payments_requests',
  CREATE_OWN_GIG_WORKERS: 'create own gig_workers',
  CREATE_OWN_IBAN_REQUESTS: 'create own IBAN_requests',
  CREATE_OWN_INVOICE_REQUESTS: 'create own invoice_requests',
  CREATE_OWN_INVOICES: 'create own invoices',
  CREATE_OWN_MSA_DOCUMENTS: 'create own msa documents',
  CREATE_OWN_OTHER_DOCUMENTS: 'create own other documents',
  CREATE_OWN_PAYROLL_DOCUMENTS: 'create own payroll documents',
  CREATE_OWN_PAYROLL_FINAL: 'create own payroll_final',
  CREATE_OWN_PAYROLLS: 'create own payrolls',
  CREATE_OWN_PAYSLIP_DOCUMENTS: 'create own payslip documents',
  CREATE_OWN_PAYSLIP_EOR_DOCUMENTS: 'create own payslip eor documents',
  CREATE_OWN_PAYSLIP_PAYROLL_SEAT_DOCUMENTS: 'create own payslip payroll_seat documents',
  CREATE_OWN_PAYSLIP_B2C_DOCUMENTS: 'create own payslip b2c documents',
  CREATE_OWN_PAYSLIP_CONTRACTOR_DOCUMENTS: 'create own payslip contractor documents',
  CREATE_OWN_PAYSLIPS: 'create own payslips',
  CREATE_OWN_PREPAYMENTS: 'create own prepayments',
  CREATE_OWN_SUBSCRIPTION: 'create own subscription',
  CREATE_OWN_TASKS: 'create own tasks',
  CREATE_OWN_TEAM_MEMBERS: 'create own teamMembers',
  CREATE_OWN_TERMINATION: 'create own termination',
  CREATE_OWN_TRANSACTIONS: 'create own transactions',
  CREATE_OWN_USER_VERIFICATION_REQUESTS: 'create own user_verification_requests',
  CREATE_OWN_WALLET_TOP_UP_REQUESTS: 'create own wallet_top_up_requests',
  CREATE_OWN_WALLETS: 'create own wallets',
  CREATE_OWN_WITHDRAWALS: 'create own withdrawals',
  CREATE_SELF_ALERTS: 'create self alerts',
  CREATE_SELF_ANNUAL_REPORT_DOCUMENTS: 'create self annual_report documents',
  CREATE_SELF_ANNEXE_DOCUMENTS: 'create self annexe documents',
  CREATE_SELF_BANK_DETAILS: 'create self bankDetails',
  CREATE_SELF_CARD_ORDER_REQUESTS: 'create self card_order_requests',
  CREATE_SELF_CARD_REQUESTS: 'create self card_requests',
  CREATE_SELF_CARD_TOP_UP_REQUESTS: 'create self card_top_up_requests',
  CREATE_SELF_CARDS: 'create self cards',
  CREATE_SELF_CONTACTS: 'create self contacts',
  CREATE_SELF_CONTRACT_DOCUMENTS: 'create self contract documents',
  CREATE_SELF_CONTRACTOR_PAYMENTS: 'create self contractor_payments',
  CREATE_SELF_EXPENSES: 'create self expenses',
  CREATE_SELF_GIG_FEE: 'create self gig_fee',
  CREATE_SELF_GIG_PAYMENTS_REQUESTS: 'create self gig_payments_requests',
  CREATE_SELF_IBAN_REQUESTS: 'create self IBAN_requests',
  CREATE_SELF_INVOICE_REQUESTS: 'create self invoice_requests',
  CREATE_SELF_INVOICES: 'create self invoices',
  CREATE_SELF_KNOWLEDGEBASE_SESSIONS: 'create self knowledgebase_sessions',
  CREATE_SELF_MSA_DOCUMENTS: 'create self msa documents',
  CREATE_SELF_OTHER_DOCUMENTS: 'create self other documents',
  CREATE_SELF_PAYROLL_DOCUMENTS: 'create self payroll documents',
  CREATE_SELF_PAYROLL_FINAL: 'create self payroll_final',
  CREATE_SELF_PAYROLLS: 'create self payrolls',
  CREATE_SELF_PAYSLIP_DOCUMENTS: 'create self payslip documents',
  CREATE_SELF_PAYSLIP_EOR_DOCUMENTS: 'create self payslip eor documents',
  CREATE_SELF_PAYSLIP_PAYROLL_SEAT_DOCUMENTS: 'create self payslip payroll_seat documents',
  CREATE_SELF_PAYSLIP_B2C_DOCUMENTS: 'create self payslip b2c documents',
  CREATE_SELF_PAYSLIP_CONTRACTOR_DOCUMENTS: 'create self payslip contractor documents',
  CREATE_SELF_PAYSLIPS: 'create self payslips',
  CREATE_SELF_PREPAYMENTS: 'create self prepayments',
  CREATE_SELF_SUBSCRIPTION: 'create self subscription',
  CREATE_SELF_TASKS: 'create self tasks',
  CREATE_SELF_TRANSACTIONS: 'create self transactions',
  CREATE_SELF_USER_VERIFICATION_REQUESTS: 'create self user_verification_requests',
  CREATE_SELF_WALLET_TOP_UP_REQUESTS: 'create self wallet_top_up_requests',
  CREATE_SELF_WALLETS: 'create self wallets',
  CREATE_SELF_WITHDRAWALS: 'create self withdrawals',
  CREATE_TEAM_CONTRACTOR_PAYMENTS: 'create team contractor_payments',
  CREATE_TRIAL_EMPLOYERS: 'create trial employers',
  CREATE_URL_XERO: 'create url xero',

  // Delete permissions
  DELETE_ANY_CONTRACTORS: 'delete any contractors',
  DELETE_ANY_EMPLOYEES: 'delete any employees',
  DELETE_ANY_EMPLOYMENT_DETAILS: 'delete any employment_details',
  DELETE_ANY_MSA_DETAILS: 'delete any msa_details',
  DELETE_ANY_ORGANISATIONS: 'delete any organisations',
  DELETE_ANY_USERS: 'delete any users',
  DELETE_OWN_CONTRACTORS: 'delete own contractors',
  DELETE_OWN_EMPLOYEES: 'delete own employees',
  DELETE_OWN_EMPLOYMENT_DETAILS: 'delete own employment_details',
  DELETE_OWN_MSA_DETAILS: 'delete own msa_details',
  DELETE_OWN_ORGANISATIONS: 'delete own organisations',
  DELETE_OWN_USERS: 'delete own users',
  DELETE_ANY_ADD_DEDUCT_FUNDS: 'delete any add_deduct_funds',
  DELETE_ANY_ADMIN_BATCH_DEDUCTIONS: 'delete any admin_batch_deductions',
  DELETE_ANY_ALERTS: 'delete any alerts',
  DELETE_ANY_ANNUAL_REPORT_DOCUMENTS: 'delete any annual_report documents',
  DELETE_ANY_ANNEXE_DOCUMENTS: 'delete any annexe documents',
  DELETE_ANY_B2C_USERS: 'delete any b2c_users',
  DELETE_ANY_BANK_DETAILS: 'delete any bankDetails',
  DELETE_ANY_CARD_ORDER_REQUESTS: 'delete any card_order_requests',
  DELETE_ANY_CARD_REQUESTS: 'delete any card_requests',
  DELETE_ANY_CARD_TOP_UP_REQUESTS: 'delete any card_top_up_requests',
  DELETE_ANY_CARDS: 'delete any cards',
  DELETE_ANY_CONTACTS: 'delete any contacts',
  DELETE_ANY_CONTRACT_DOCUMENTS: 'delete any contract documents',
  DELETE_ANY_CONTRACTOR_PAYMENTS: 'delete any contractor_payments',
  DELETE_ANY_COUNTRY_ANNUAL_LEAVES: 'delete any country_annual_leaves',
  DELETE_ANY_COUNTRY_EMPLOYMENT_INFO: 'delete any country_employment_info',
  DELETE_ANY_ENTITIES: 'delete any entities',
  DELETE_ANY_EXPENSES: 'delete any expenses',
  DELETE_ANY_EXPENSE_GUIDELINES: 'delete any expense_guidelines',
  DELETE_ANY_GIG_PAYMENTS_REQUESTS: 'delete any gig_payments_requests',
  DELETE_ANY_GIG_WORKERS: 'delete any gig_workers',
  DELETE_ANY_IBAN_REQUESTS: 'delete any IBAN_requests',
  DELETE_ANY_INVOICE_DOCUMENTS: 'delete any invoice documents',
  DELETE_ANY_INVOICE_REQUESTS: 'delete any invoice_requests',
  DELETE_ANY_INVOICES: 'delete any invoices',
  DELETE_ANY_MSA_DOCUMENTS: 'delete any msa documents',
  DELETE_ANY_ORGANISATION_FEES: 'delete any organisation_fees',
  DELETE_ANY_OTHER_DOCUMENTS: 'delete any other documents',
  DELETE_ANY_PAYDAYS: 'delete any paydays',
  DELETE_ANY_PAYROLL_DOCUMENTS: 'delete any payroll documents',
  DELETE_ANY_PAYROLLS: 'delete any payrolls',
  DELETE_ANY_PAYROLL_FINAL: 'delete any payroll_final',
  DELETE_ANY_PAYSLIP_DOCUMENTS: 'delete any payslip documents',
  DELETE_ANY_PAYSLIP_EOR_DOCUMENTS: 'delete any payslip eor documents',
  DELETE_ANY_PAYSLIP_PAYROLL_SEAT_DOCUMENTS: 'delete any payslip payroll_seat documents',
  DELETE_ANY_PAYSLIP_B2C_DOCUMENTS: 'delete any payslip b2c documents',
  DELETE_ANY_PAYSLIP_CONTRACTOR_DOCUMENTS: 'delete any payslip contractor documents',
  DELETE_ANY_PAYSLIPS: 'delete any payslips',
  DELETE_ANY_PREPAYMENTS: 'delete any prepayments',
  DELETE_ANY_PRICING_ADDENDUM_DOCUMENTS: 'delete any pricing_addendum documents',
  DELETE_ANY_PUBLIC_HOLIDAYS: 'delete any public_holidays',
  DELETE_ANY_REIMBURSEMENT_GUIDELINES: 'delete any reimbursement_guidelines',
  DELETE_ANY_SUBSCRIPTION: 'delete any subscription',
  DELETE_ANY_TASKS: 'delete any tasks',
  DELETE_ANY_TEAM_MEMBERS: 'delete any teamMembers',
  DELETE_ANY_TEAMS: 'delete any teams',
  DELETE_ANY_TRANSACTIONS: 'delete any transactions',
  DELETE_ANY_USER_FEES: 'delete any user_fees',
  DELETE_ANY_USER_VERIFICATION_REQUESTS: 'delete any user_verification_requests',
  DELETE_ANY_WALLET_TOP_UP_REQUESTS: 'delete any wallet_top_up_requests',
  DELETE_ANY_WALLETS: 'delete any wallets',
  DELETE_ANY_WITHDRAWALS: 'delete any withdrawals',
  DELETE_IN_OWN_ENTITIES: 'delete in own entities',
  DELETE_IN_OWN_EXPENSES: 'delete in own expenses',
  DELETE_IN_OWN_MSA_DETAILS: 'delete in own msa_details',
  DELETE_IN_OWN_PAYROLLS: 'delete in own payrolls',
  DELETE_IN_OWN_TEAM_MEMBERS: 'delete in own teamMembers',
  DELETE_IN_OWN_TEAMS: 'delete in own teams',
  DELETE_OWN_ALERTS: 'delete own alerts',
  DELETE_OWN_ADD_DEDUCT_FUNDS: 'delete own add_deduct_funds',
  DELETE_OWN_ANNEXE_DOCUMENTS: 'delete own annexe documents',
  DELETE_OWN_BANK_DETAILS: 'delete own bankDetails',
  DELETE_OWN_CARD_ORDER_REQUESTS: 'delete own card_order_requests',
  DELETE_OWN_CARD_TOP_UP_REQUESTS: 'delete own card_top_up_requests',
  DELETE_OWN_CARDS: 'delete own cards',
  DELETE_OWN_CONTACTS: 'delete own contacts',
  DELETE_OWN_CONTRACT_DOCUMENTS: 'delete own contract documents',
  DELETE_OWN_CONTRACTOR_PAYMENTS: 'delete own contractor_payments',
  DELETE_OWN_ENTITIES: 'delete own entities',
  DELETE_OWN_EXPENSES: 'delete own expenses',
  DELETE_OWN_EXPENSE_GUIDELINES: 'delete own expense_guidelines',
  DELETE_OWN_GIG_PAYMENTS_REQUESTS: 'delete own gig_payments_requests',
  DELETE_OWN_GIG_WORKERS: 'delete own gig_workers',
  DELETE_OWN_IBAN_REQUESTS: 'delete own IBAN_requests',
  DELETE_OWN_INVOICE_REQUESTS: 'delete own invoice_requests',
  DELETE_OWN_INVOICES: 'delete own invoices',
  DELETE_OWN_MSA_DOCUMENTS: 'delete own msa documents',
  DELETE_OWN_OTHER_DOCUMENTS: 'delete own other documents',
  DELETE_OWN_PAYROLL_DOCUMENTS: 'delete own payroll documents',
  DELETE_OWN_PAYROLL_FINAL: 'delete own payroll_final',
  DELETE_OWN_PAYROLLS: 'delete own payrolls',
  DELETE_OWN_PAYSLIP_DOCUMENTS: 'delete own payslip documents',
  DELETE_OWN_PAYSLIP_EOR_DOCUMENTS: 'delete own payslip eor documents',
  DELETE_OWN_PAYSLIP_PAYROLL_SEAT_DOCUMENTS: 'delete own payslip payroll_seat documents',
  DELETE_OWN_PAYSLIP_B2C_DOCUMENTS: 'delete own payslip b2c documents',
  DELETE_OWN_PAYSLIP_CONTRACTOR_DOCUMENTS: 'delete own payslip contractor documents',
  DELETE_OWN_PAYSLIPS: 'delete own payslips',
  DELETE_OWN_PREPAYMENTS: 'delete own prepayments',
  DELETE_OWN_TASKS: 'delete own tasks',
  DELETE_OWN_TEAM_MEMBERS: 'delete own teamMembers',
  DELETE_OWN_TRANSACTIONS: 'delete own transactions',
  DELETE_OWN_USER_VERIFICATION_REQUESTS: 'delete own user_verification_requests',
  DELETE_OWN_WALLET_TOP_UP_REQUESTS: 'delete own wallet_top_up_requests',
  DELETE_OWN_WALLETS: 'delete own wallets',
  DELETE_OWN_WITHDRAWALS: 'delete own withdrawals',
  DELETE_SELF_ADD_DEDUCT_FUNDS: 'delete self add_deduct_funds',
  DELETE_SELF_ANNEXE_DOCUMENTS: 'delete self annexe documents',
  DELETE_SELF_CARD_ORDER_REQUESTS: 'delete self card_order_requests',
  DELETE_SELF_CARD_TOP_UP_REQUESTS: 'delete self card_top_up_requests',
  DELETE_SELF_CONTRACT_DOCUMENTS: 'delete self contract documents',
  DELETE_SELF_CONTRACTOR_PAYMENTS: 'delete self contractor_payments',
  DELETE_SELF_EXPENSE_GUIDELINES: 'delete self expense_guidelines',
  DELETE_SELF_GIG_PAYMENTS_REQUESTS: 'delete self gig_payments_requests',
  DELETE_SELF_IBAN_REQUESTS: 'delete self IBAN_requests',
  DELETE_SELF_INVOICE_REQUESTS: 'delete self invoice_requests',
  DELETE_SELF_MSA_DOCUMENTS: 'delete self msa documents',
  DELETE_SELF_OTHER_DOCUMENTS: 'delete self other documents',
  DELETE_SELF_PAYROLL_DOCUMENTS: 'delete self payroll documents',
  DELETE_SELF_PAYROLL_FINAL: 'delete self payroll_final',
  DELETE_SELF_PAYROLLS: 'delete self payrolls',
  DELETE_SELF_PAYSLIP_DOCUMENTS: 'delete self payslip documents',
  DELETE_SELF_PAYSLIP_EOR_DOCUMENTS: 'delete self payslip eor documents',
  DELETE_SELF_PAYSLIP_PAYROLL_SEAT_DOCUMENTS: 'delete self payslip payroll_seat documents',
  DELETE_SELF_PAYSLIP_B2C_DOCUMENTS: 'delete self payslip b2c documents',
  DELETE_SELF_PAYSLIP_CONTRACTOR_DOCUMENTS: 'delete self payslip contractor documents',
  DELETE_SELF_TASKS: 'delete self tasks',
  DELETE_SELF_USER_VERIFICATION_REQUESTS: 'delete self user_verification_requests',
  DELETE_SELF_WALLET_TOP_UP_REQUESTS: 'delete self wallet_top_up_requests',
  DELETE_SELF_WITHDRAWALS: 'delete self withdrawals',
  DELETE_TEAM_CONTRACTOR_PAYMENTS: 'delete team contractor_payments',

  // Update permissions
  UPDATE_ANY_CONTRACTORS: 'update any contractors',
  UPDATE_ANY_EMPLOYEES: 'update any employees',
  UPDATE_ANY_EMPLOYMENT_DETAILS: 'update any employment_details',
  UPDATE_ANY_MSA_DETAILS: 'update any msa_details',
  UPDATE_ANY_MSA_DOCUMENTS: 'update any msa documents',
  UPDATE_ANY_ORGANISATIONS: 'update any organisations',
  UPDATE_ANY_USERS: 'update any users',
  UPDATE_OWN_CONTRACTORS: 'update own contractors',
  UPDATE_OWN_EMPLOYEES: 'update own employees',
  UPDATE_OWN_EMPLOYMENT_DETAILS: 'update own employment_details',
  UPDATE_OWN_MSA_DETAILS: 'update own msa_details',
  UPDATE_OWN_ORGANISATIONS: 'update own organisations',
  UPDATE_OWN_USERS: 'update own users',
  UPDATE_SELF_CONTRACTORS: 'update self contractors',
  UPDATE_SELF_EMPLOYEES: 'update self employees',
  UPDATE_SELF_USERS: 'update self users',
  UPDATE_ANY_ADD_DEDUCT_FUNDS: 'update any add_deduct_funds',
  UPDATE_ANY_ADMIN_BATCH_DEDUCTIONS: 'update any admin_batch_deductions',
  UPDATE_ANY_ALERTS: 'update any alerts',
  UPDATE_ANY_ANNEXE_DOCUMENTS: 'update any annexe documents',
  UPDATE_ANY_ANNUAL_REPORT_DOCUMENTS: 'update any annual_report documents',
  UPDATE_ANY_B2C_USERS: 'update any b2c_users',
  UPDATE_ANY_BANK_DETAILS: 'update any bankDetails',
  UPDATE_ANY_CARD_ORDER_REQUESTS: 'update any card_order_requests',
  UPDATE_ANY_CARD_REQUESTS: 'update any card_requests',
  UPDATE_ANY_CARD_TOP_UP_REQUESTS: 'update any card_top_up_requests',
  UPDATE_ANY_CARDS: 'update any cards',
  UPDATE_ANY_CONTACTS: 'update any contacts',
  UPDATE_ANY_CONTRACTOR_PAYMENTS: 'update any contractor_payments',
  UPDATE_ANY_CONTRACT_DOCUMENTS: 'update any contract documents',
  UPDATE_ANY_COUNTRY_ANNUAL_LEAVES: 'update any country_annual_leaves',
  UPDATE_ANY_COUNTRY_EMPLOYMENT_INFO: 'update any country_employment_info',
  UPDATE_ANY_ENTITIES: 'update any entities',
  UPDATE_ANY_EXPENSES: 'update any expenses',
  UPDATE_ANY_EXPENSE_GUIDELINES: 'update any expense_guidelines',
  UPDATE_ANY_GIG_FEE: 'update any gig_fee',
  UPDATE_ANY_GIG_PAYMENTS_REQUESTS: 'update any gig_payments_requests',
  UPDATE_ANY_GIG_WORKERS: 'update any gig_workers',
  UPDATE_ANY_IBAN_REQUESTS: 'update any IBAN_requests',
  UPDATE_ANY_INVOICE_REQUESTS: 'update any invoice_requests',
  UPDATE_ANY_INVOICES: 'update any invoices',
  UPDATE_ANY_ORGANISATION_FEES: 'update any organisation_fees',
  UPDATE_ANY_PAYDAYS: 'update any paydays',
  UPDATE_ANY_PAYROLLS: 'update any payrolls',
  UPDATE_ANY_PAYROLL_FINAL: 'update any payroll_final',
  UPDATE_ANY_PAYSLIP_DOCUMENTS: 'update any payslip documents',
  UPDATE_ANY_PAYSLIP_EOR_DOCUMENTS: 'update any payslip eor documents',
  UPDATE_ANY_PAYSLIP_PAYROLL_SEAT_DOCUMENTS: 'update any payslip payroll_seat documents',
  UPDATE_ANY_PAYSLIP_B2C_DOCUMENTS: 'update any payslip b2c documents',
  UPDATE_ANY_PAYSLIP_CONTRACTOR_DOCUMENTS: 'update any payslip contractor documents',
  UPDATE_ANY_PAYSLIPS: 'update any payslips',
  UPDATE_ANY_PREPAYMENTS: 'update any prepayments',
  UPDATE_ANY_PRICING_ADDENDUM_DOCUMENTS: 'update any pricing_addendum documents',
  UPDATE_ANY_PUBLIC_HOLIDAYS: 'update any public_holidays',
  UPDATE_ANY_REIMBURSEMENT_GUIDELINES: 'update any reimbursement_guidelines',
  UPDATE_ANY_SUBSCRIPTION: 'update any subscription',
  UPDATE_ANY_TASKS: 'update any tasks',
  UPDATE_ANY_TEAM_MEMBERS: 'update any teamMembers',
  UPDATE_ANY_TEAMS: 'update any teams',
  UPDATE_ANY_TRANSACTIONS: 'update any transactions',
  UPDATE_ANY_USER_FEES: 'update any user_fees',
  UPDATE_ANY_USER_VERIFICATION_REQUESTS: 'update any user_verification_requests',
  UPDATE_ANY_WALLET_TOP_UP_REQUESTS: 'update any wallet_top_up_requests',
  UPDATE_ANY_WALLETS: 'update any wallets',
  UPDATE_ANY_WITHDRAWALS: 'update any withdrawals',
  UPDATE_FEE_WITHDRAWALS: 'update fee withdrawals',
  UPDATE_IN_OWN_CONTRACTORS: 'update in own contractors',
  UPDATE_IN_OWN_EMPLOYEES: 'update in own employees',
  UPDATE_IN_OWN_EMPLOYMENT_DETAILS: 'update in own employment_details',
  UPDATE_IN_OWN_ENTITIES: 'update in own entities',
  UPDATE_IN_OWN_EXPENSES: 'update in own expenses',
  UPDATE_IN_OWN_GIG_WORKERS: 'update in own gig_workers',
  UPDATE_IN_OWN_INVOICES: 'update in own invoices',
  UPDATE_IN_OWN_MSA_DETAILS: 'update in own msa_details',
  UPDATE_IN_OWN_PAYROLLS: 'update in own payrolls',
  UPDATE_IN_OWN_TEAM_MEMBERS: 'update in own teamMembers',
  UPDATE_IN_OWN_TEAMS: 'update in own teams',
  UPDATE_IN_OWN_USERS: 'update in own users',
  UPDATE_OWN_ALERTS: 'update own alerts',
  UPDATE_OWN_BANK_DETAILS: 'update own bankDetails',
  UPDATE_OWN_CARD_ORDER_REQUESTS: 'update own card_order_requests',
  UPDATE_OWN_CARD_TOP_UP_REQUESTS: 'update own card_top_up_requests',
  UPDATE_OWN_CARDS: 'update own cards',
  UPDATE_OWN_CONTACTS: 'update own contacts',
  UPDATE_OWN_CONTRACTOR_PAYMENTS: 'update own contractor_payments',
  UPDATE_OWN_ENTITIES: 'update own entities',
  UPDATE_OWN_EXPENSES: 'update own expenses',
  UPDATE_OWN_GIG_FEE: 'update own gig_fee',
  UPDATE_OWN_GIG_PAYMENTS_REQUESTS: 'update own gig_payments_requests',
  UPDATE_OWN_GIG_WORKERS: 'update own gig_workers',
  UPDATE_OWN_IBAN_REQUESTS: 'update own IBAN_requests',
  UPDATE_OWN_INVOICE_REQUESTS: 'update own invoice_requests',
  UPDATE_OWN_INVOICES: 'update own invoices',
  UPDATE_OWN_PAYROLL_FINAL: 'update own payroll_final',
  UPDATE_OWN_PAYSLIPS: 'update own payslips',
  UPDATE_OWN_PAYROLLS: 'update own payrolls',
  UPDATE_OWN_PREPAYMENTS: 'update own prepayments',
  UPDATE_OWN_SUBSCRIPTION: 'update own subscription',
  UPDATE_OWN_TASKS: 'update own tasks',
  UPDATE_OWN_TEAM_MEMBERS: 'update own teamMembers',
  UPDATE_OWN_TEAMS: 'update own teams',
  UPDATE_OWN_TRANSACTIONS: 'update own transactions',
  UPDATE_OWN_USER_VERIFICATION_REQUESTS: 'update own user_verification_requests',
  UPDATE_OWN_WALLET_TOP_UP_REQUESTS: 'update own wallet_top_up_requests',
  UPDATE_OWN_WALLETS: 'update own wallets',
  UPDATE_OWN_WITHDRAWALS: 'update own withdrawals',
  UPDATE_SELF_ALERTS: 'update self alerts',
  UPDATE_SELF_B2C_USERS: 'update self b2c_users',
  UPDATE_SELF_BANK_DETAILS: 'update self bankDetails',
  UPDATE_SELF_CARD_ORDER_REQUESTS: 'update self card_order_requests',
  UPDATE_SELF_CARD_TOP_UP_REQUESTS: 'update self card_top_up_requests',
  UPDATE_SELF_CARDS: 'update self cards',
  UPDATE_SELF_CONTACTS: 'update self contacts',
  UPDATE_SELF_CONTRACTOR_PAYMENTS: 'update self contractor_payments',
  UPDATE_SELF_EXPENSES: 'update self expenses',
  UPDATE_SELF_GIG_FEE: 'update self gig_fee',
  UPDATE_SELF_GIG_PAYMENTS_REQUESTS: 'update self gig_payments_requests',
  UPDATE_SELF_GIG_WORKERS: 'update self gig_workers',
  UPDATE_SELF_IBAN_REQUESTS: 'update self IBAN_requests',
  UPDATE_SELF_INVOICE_REQUESTS: 'update self invoice_requests',
  UPDATE_SELF_INVOICES: 'update self invoices',
  UPDATE_SELF_PAYROLL_FINAL: 'update self payroll_final',
  UPDATE_SELF_PAYROLLS: 'update self payrolls',
  UPDATE_SELF_PAYSLIPS: 'update self payslips',
  UPDATE_SELF_PREPAYMENTS: 'update self prepayments',
  UPDATE_SELF_SUBSCRIPTION: 'update self subscription',
  UPDATE_SELF_TASKS: 'update self tasks',
  UPDATE_SELF_TRANSACTIONS: 'update self transactions',
  UPDATE_SELF_USER_VERIFICATION_REQUESTS: 'update self user_verification_requests',
  UPDATE_SELF_WALLET_TOP_UP_REQUESTS: 'update self wallet_top_up_requests',
  UPDATE_SELF_WALLETS: 'update self wallets',
  UPDATE_SELF_WITHDRAWALS: 'update self withdrawals',
  UPDATE_TEAM_CONTRACTOR_PAYMENTS: 'update team contractor_payments',

  // View permissions
  VIEW_ANY_CONTRACTORS: 'view any contractors',
  VIEW_ANY_EMPLOYEES: 'view any employees',
  VIEW_ANY_EMPLOYMENT_DETAILS: 'view any employment_details',
  VIEW_ANY_MSA_DETAILS: 'view any msa_details',
  VIEW_ANY_ORGANISATIONS: 'view any organisations',
  VIEW_ANY_USERS: 'view any users',
  VIEW_OWN_CONTRACTORS: 'view own contractors',
  VIEW_OWN_EMPLOYEES: 'view own employees',
  VIEW_OWN_EMPLOYMENT_DETAILS: 'view own employment_details',
  VIEW_OWN_MSA_DETAILS: 'view own msa_details',
  VIEW_OWN_ORGANISATIONS: 'view own organisations',
  VIEW_OWN_USERS: 'view own users',
  VIEW_ANY_WALLETS: 'view any wallets',
  VIEW_ANY_ADD_DEDUCT_FUNDS: 'view any add_deduct_funds',
  VIEW_ANY_ADMIN_BATCH_DEDUCTIONS: 'view any admin_batch_deductions',
  VIEW_ANY_ALERTS: 'view any alerts',
  VIEW_ANY_ANNUAL_REPORT_DOCUMENTS: 'view any annual_report documents',
  VIEW_ANY_ANNEXE_DOCUMENTS: 'view any annexe documents',
  VIEW_ANY_B2C_USERS: 'view any b2c_users',
  VIEW_ANY_BANK_DETAILS: 'view any bankDetails',
  VIEW_ANY_CARD_ORDER_REQUESTS: 'view any card_order_requests',
  VIEW_ANY_CARD_REQUESTS: 'view any card_requests',
  VIEW_ANY_CARD_TOP_UP_REQUESTS: 'view any card_top_up_requests',
  VIEW_ANY_CARDS: 'view any cards',
  VIEW_ANY_CONTACTS: 'view any contacts',
  VIEW_ANY_CONTRACT_DOCUMENTS: 'view any contract documents',
  VIEW_ANY_CONTRACTOR_PAYMENTS: 'view any contractor_payments',
  VIEW_ANY_COUNTRY_ANNUAL_LEAVES: 'view any country_annual_leaves',
  VIEW_ANY_COUNTRY_EMPLOYMENT_INFO: 'view any country_employment_info',
  VIEW_ANY_ENTITIES: 'view any entities',
  VIEW_ANY_EXPENSES: 'view any expenses',
  VIEW_ANY_EXPENSE_GUIDELINES: 'view any expense_guidelines',
  VIEW_ANY_GIG_FEE: 'view any gig_fee',
  VIEW_ANY_GIG_PAYMENTS_REQUESTS: 'view any gig_payments_requests',
  VIEW_ANY_GIG_WORKERS: 'view any gig_workers',
  VIEW_ANY_IBAN_REQUESTS: 'view any IBAN_requests',
  VIEW_ANY_INVOICE_DOCUMENTS: 'view any invoice documents',
  VIEW_ANY_INVOICE_REQUESTS: 'view any invoice_requests',
  VIEW_ANY_INVOICES: 'view any invoices',
  VIEW_ANY_KNOWLEDGEBASE_SESSIONS: 'view any knowledgebase_sessions',
  VIEW_ANY_KYC_VERIFICATIONS: 'view any kyc_verifications',
  VIEW_ANY_MSA_DOCUMENTS: 'view any msa documents',
  VIEW_ANY_ONBOARDING_FORM_DOCUMENTS: 'view any onboarding_form documents',
  VIEW_ANY_ORGANISATION_FEES: 'view any organisation_fees',
  VIEW_ANY_OTHER_DOCUMENTS: 'view any other documents',
  VIEW_ANY_PAYDAYS: 'view any paydays',
  VIEW_ANY_PAYROLL_DOCUMENTS: 'view any payroll documents',
  VIEW_ANY_PAYROLLS: 'view any payrolls',
  VIEW_ANY_PAYROLL_FINAL: 'view any payroll_final',
  VIEW_ANY_PAYSLIP_DOCUMENTS: 'view any payslip documents',
  VIEW_ANY_PAYSLIP_EOR_DOCUMENTS: 'view any payslip eor documents',
  VIEW_ANY_PAYSLIP_PAYROLL_SEAT_DOCUMENTS: 'view any payslip payroll_seat documents',
  VIEW_ANY_PAYSLIP_B2C_DOCUMENTS: 'view any payslip b2c documents',
  VIEW_ANY_PAYSLIP_CONTRACTOR_DOCUMENTS: 'view any payslip contractor documents',
  VIEW_ANY_PAYSLIPS: 'view any payslips',
  VIEW_ANY_PREPAYMENTS: 'view any prepayments',
  VIEW_ANY_PRICING_ADDENDUM_DOCUMENTS: 'view any pricing_addendum documents',
  VIEW_ANY_PUBLIC_HOLIDAYS: 'view any public_holidays',
  VIEW_ANY_REIMBURSEMENT_GUIDELINES: 'view any reimbursement_guidelines',
  VIEW_ANY_REPORTS: 'view any reports',
  VIEW_ANY_STATEMENT_OF_ACCOUNT_DOCUMENTS: 'view any statement_of_account documents',
  VIEW_ANY_SUBSCRIPTION: 'view any subscription',
  VIEW_ANY_SUPPORTING_DOCUMENTS_DOCUMENTS: 'view any supporting_documents documents',
  VIEW_ANY_TASKS: 'view any tasks',
  VIEW_ANY_TEAM_MEMBERS: 'view any teamMembers',
  VIEW_ANY_TEAMS: 'view any teams',
  VIEW_ANY_TRANSACTIONS: 'view any transactions',
  VIEW_ANY_USER_FEES: 'view any user_fees',
  VIEW_ANY_USER_VERIFICATION_REQUESTS: 'view any user_verification_requests',
  VIEW_ANY_WALLET_TOP_UP_REQUESTS: 'view any wallet_top_up_requests',
  VIEW_ANY_WITHDRAWALS: 'view any withdrawals',
  VIEW_IN_OWN_ANNUAL_REPORT_DOCUMENTS: 'view in own annual_report documents',
  VIEW_IN_OWN_ANNEXE_DOCUMENTS: 'view in own annexe documents',
  VIEW_IN_OWN_CONTRACT_DOCUMENTS: 'view in own contract documents',
  VIEW_IN_OWN_CONTRACTORS: 'view in own contractors',
  VIEW_IN_OWN_COUNTRY_EMPLOYMENT_INFO: 'view in own country_employment_info',
  VIEW_IN_OWN_EMPLOYEES: 'view in own employees',
  VIEW_IN_OWN_EMPLOYMENT_DETAILS: 'view in own employment_details',
  VIEW_IN_OWN_ENTITIES: 'view in own entities',
  VIEW_IN_OWN_EXPENSES: 'view in own expenses',
  VIEW_IN_OWN_GIG_WORKERS: 'view in own gig_workers',
  VIEW_IN_OWN_INVOICE_DOCUMENTS: 'view in own invoice documents',
  VIEW_IN_OWN_INVOICES: 'view in own invoices',
  VIEW_IN_OWN_MSA_DOCUMENTS: 'view in own msa documents',
  VIEW_IN_OWN_MSA_DETAILS: 'view in own msa_details',
  VIEW_IN_OWN_ONBOARDING_FORM_DOCUMENTS: 'view in own onboarding_form documents',
  VIEW_IN_OWN_OTHER_DOCUMENTS: 'view in own other documents',
  VIEW_IN_OWN_PAYROLL_DOCUMENTS: 'view in own payroll documents',
  VIEW_IN_OWN_PAYROLLS: 'view in own payrolls',
  VIEW_IN_OWN_PAYSLIP_DOCUMENTS: 'view in own payslip documents',
  VIEW_IN_OWN_PAYSLIP_EOR_DOCUMENTS: 'view in own payslip eor documents',
  VIEW_IN_OWN_PAYSLIP_PAYROLL_SEAT_DOCUMENTS: 'view in own payslip payroll_seat documents',
  VIEW_IN_OWN_PAYSLIP_B2C_DOCUMENTS: 'view in own payslip b2c documents',
  VIEW_IN_OWN_PAYSLIP_CONTRACTOR_DOCUMENTS: 'view in own payslip contractor documents',
  VIEW_IN_OWN_PREPAYMENTS: 'view in own prepayments',
  VIEW_IN_OWN_PRICING_ADDENDUM_DOCUMENTS: 'view in own pricing_addendum documents',
  VIEW_IN_OWN_STATEMENT_OF_ACCOUNT_DOCUMENTS: 'view in own statement_of_account documents',
  VIEW_IN_OWN_SUPPORTING_DOCUMENTS_DOCUMENTS: 'view in own supporting_documents documents',
  VIEW_IN_OWN_TEAM_MEMBERS: 'view in own teamMembers',
  VIEW_IN_OWN_TEAMS: 'view in own teams',
  VIEW_IN_OWN_USERS: 'view in own users',
  VIEW_IN_OWN_WALLETS: 'view in own wallets',
  VIEW_NT_TEAM_ORGANISATIONS: 'view nt team organisations',
  VIEW_OWN_ALERTS: 'view own alerts',
  VIEW_OWN_ANNUAL_REPORT_DOCUMENTS: 'view own annual_report documents',
  VIEW_OWN_ANNEXE_DOCUMENTS: 'view own annexe documents',
  VIEW_OWN_BANK_DETAILS: 'view own bankDetails',
  VIEW_OWN_CARD_ORDER_REQUESTS: 'view own card_order_requests',
  VIEW_OWN_CARD_TOP_UP_REQUESTS: 'view own card_top_up_requests',
  VIEW_OWN_CARDS: 'view own cards',
  VIEW_OWN_CONTACTS: 'view own contacts',
  VIEW_OWN_CONTRACT_DOCUMENTS: 'view own contract documents',
  VIEW_OWN_CONTRACTOR_PAYMENTS: 'view own contractor_payments',
  VIEW_OWN_COUNTRY_ANNUAL_LEAVES: 'view own country_annual_leaves',
  VIEW_OWN_COUNTRY_EMPLOYMENT_INFO: 'view own country_employment_info',
  VIEW_OWN_ENTITIES: 'view own entities',
  VIEW_OWN_EXPENSES: 'view own expenses',
  VIEW_OWN_GIG_FEE: 'view own gig_fee',
  VIEW_OWN_GIG_PAYMENTS_REQUESTS: 'view own gig_payments_requests',
  VIEW_OWN_GIG_WORKERS: 'view own gig_workers',
  VIEW_OWN_IBAN_REQUESTS: 'view own IBAN_requests',
  VIEW_OWN_INVOICE_DOCUMENTS: 'view own invoice documents',
  VIEW_OWN_INVOICE_REQUESTS: 'view own invoice_requests',
  VIEW_OWN_INVOICES: 'view own invoices',
  VIEW_OWN_KYC_VERIFICATIONS: 'view own kyc_verifications',
  VIEW_OWN_MSA_DOCUMENTS: 'view own msa documents',
  VIEW_OWN_ONBOARDING_FORM_DOCUMENTS: 'view own onboarding_form documents',
  VIEW_OWN_OTHER_DOCUMENTS: 'view own other documents',
  VIEW_OWN_PAYROLL_FINAL: 'view own payroll_final',
  VIEW_OWN_PAYROLL_DOCUMENTS: 'view own payroll documents',
  VIEW_OWN_PAYROLLS: 'view own payrolls',
  VIEW_OWN_PAYSLIP_DOCUMENTS: 'view own payslip documents',
  VIEW_OWN_PAYSLIP_EOR_DOCUMENTS: 'view own payslip eor documents',
  VIEW_OWN_PAYSLIP_PAYROLL_SEAT_DOCUMENTS: 'view own payslip payroll_seat documents',
  VIEW_OWN_PAYSLIP_B2C_DOCUMENTS: 'view own payslip b2c documents',
  VIEW_OWN_PAYSLIP_CONTRACTOR_DOCUMENTS: 'view own payslip contractor documents',
  VIEW_OWN_PAYSLIPS: 'view own payslips',
  VIEW_OWN_PREPAYMENTS: 'view own prepayments',
  VIEW_OWN_PRICING_ADDENDUM_DOCUMENTS: 'view own pricing_addendum documents',
  VIEW_OWN_PUBLIC_HOLIDAYS: 'view own public_holidays',
  VIEW_OWN_REPORTS: 'view own reports',
  VIEW_OWN_STATEMENT_OF_ACCOUNT_DOCUMENTS: 'view own statement_of_account documents',
  VIEW_OWN_SUBSCRIPTION: 'view own subscription',
  VIEW_OWN_SUPPORTING_DOCUMENTS_DOCUMENTS: 'view own supporting_documents documents',
  VIEW_OWN_TASKS: 'view own tasks',
  VIEW_OWN_TEAM_MEMBERS: 'view own teamMembers',
  VIEW_OWN_TEAMS: 'view own teams',
  VIEW_OWN_TRANSACTIONS: 'view own transactions',
  VIEW_OWN_USER_VERIFICATION_REQUESTS: 'view own user_verification_requests',
  VIEW_OWN_WALLET_TOP_UP_REQUESTS: 'view own wallet_top_up_requests',
  VIEW_OWN_WALLETS: 'view own wallets',
  VIEW_OWN_WITHDRAWALS: 'view own withdrawals',
  VIEW_SELF_ALERTS: 'view self alerts',
  VIEW_SELF_ANNUAL_REPORT_DOCUMENTS: 'view self annual_report documents',
  VIEW_SELF_ANNEXE_DOCUMENTS: 'view self annexe documents',
  VIEW_SELF_B2C_USERS: 'view self b2c_users',
  VIEW_SELF_BANK_DETAILS: 'view self bankDetails',
  VIEW_SELF_CARD_ORDER_REQUESTS: 'view self card_order_requests',
  VIEW_SELF_CARD_REQUESTS: 'view self card_requests',
  VIEW_SELF_CARD_TOP_UP_REQUESTS: 'view self card_top_up_requests',
  VIEW_SELF_CARDS: 'view self cards',
  VIEW_SELF_CONTACTS: 'view self contacts',
  VIEW_SELF_CONTRACT_DOCUMENTS: 'view self contract documents',
  VIEW_SELF_CONTRACTOR_PAYMENTS: 'view self contractor_payments',
  VIEW_SELF_CONTRACTORS: 'view self contractors',
  VIEW_SELF_EMPLOYEES: 'view self employees',
  VIEW_SELF_EMPLOYMENT_DETAILS: 'view self employment_details',
  VIEW_SELF_EXPENSES: 'view self expenses',
  VIEW_SELF_GIG_FEE: 'view self gig_fee',
  VIEW_SELF_GIG_PAYMENTS_REQUESTS: 'view self gig_payments_requests',
  VIEW_SELF_GIG_WORKERS: 'view self gig_workers',
  VIEW_SELF_IBAN_REQUESTS: 'view self IBAN_requests',
  VIEW_SELF_INVOICE_DOCUMENTS: 'view self invoice documents',
  VIEW_SELF_INVOICE_REQUESTS: 'view self invoice_requests',
  VIEW_SELF_INVOICES: 'view self invoices',
  VIEW_SELF_KNOWLEDGEBASE_SESSIONS: 'view self knowledgebase_sessions',
  VIEW_SELF_KYC_VERIFICATIONS: 'view self kyc_verifications',
  VIEW_SELF_MSA_DOCUMENTS: 'view self msa documents',
  VIEW_SELF_ONBOARDING_FORM_DOCUMENTS: 'view self onboarding_form documents',
  VIEW_SELF_OTHER_DOCUMENTS: 'view self other documents',
  VIEW_SELF_PAYROLL_FINAL: 'view self payroll_final',
  VIEW_SELF_PAYROLL_BONUS_DOCUMENTS: 'view self payroll bonus documents',
  VIEW_SELF_PAYROLL_CONTRACTED_ANNUAL_LEAVE_DOCUMENTS:
    'view self payroll contracted_annual_leave documents',
  VIEW_SELF_PAYROLL_DOCUMENTS: 'view self payroll documents',
  VIEW_SELF_PAYROLL_EXPENSE_DOCUMENTS: 'view self payroll expense documents',
  VIEW_SELF_PAYROLL_OTHER_DOCUMENTS: 'view self payroll other documents',
  VIEW_SELF_PAYROLL_OVERTIME_HOURS_DOCUMENTS: 'view self payroll overtime_hours documents',
  VIEW_SELF_PAYROLL_SALARY_CHANGE_DOCUMENTS: 'view self payroll salary_change documents',
  VIEW_SELF_PAYROLL_SICK_LEAVE_DOCUMENTS: 'view self payroll sick_leave documents',
  VIEW_SELF_PAYROLL_UNPAID_LEAVE_DOCUMENTS: 'view self payroll unpaid_leave documents',
  VIEW_SELF_PAYROLLS: 'view self payrolls',
  VIEW_SELF_PAYSLIP_DOCUMENTS: 'view self payslip documents',
  VIEW_SELF_PAYSLIP_EOR_DOCUMENTS: 'view self payslip eor documents',
  VIEW_SELF_PAYSLIP_PAYROLL_SEAT_DOCUMENTS: 'view self payslip payroll_seat documents',
  VIEW_SELF_PAYSLIP_B2C_DOCUMENTS: 'view self payslip b2c documents',
  VIEW_SELF_PAYSLIP_CONTRACTOR_DOCUMENTS: 'view self payslip contractor documents',
  VIEW_SELF_PAYSLIPS: 'view self payslips',
  VIEW_SELF_PREPAYMENTS: 'view self prepayments',
  VIEW_SELF_PRICING_ADDENDUM_DOCUMENTS: 'view self pricing_addendum documents',
  VIEW_SELF_STATEMENT_OF_ACCOUNT_DOCUMENTS: 'view self statement_of_account documents',
  VIEW_SELF_SUBSCRIPTION: 'view self subscription',
  VIEW_SELF_SUPPORTING_DOCUMENTS_DOCUMENTS: 'view self supporting_documents documents',
  VIEW_SELF_TASKS: 'view self tasks',
  VIEW_SELF_TEAM_MEMBERS: 'view self teamMembers',
  VIEW_SELF_TEAMS: 'view self teams',
  VIEW_SELF_TRANSACTIONS: 'view self transactions',
  VIEW_SELF_USERS: 'view self users',
  VIEW_SELF_USER_VERIFICATION_REQUESTS: 'view self user_verification_requests',
  VIEW_SELF_WALLET_TOP_UP_REQUESTS: 'view self wallet_top_up_requests',
  VIEW_SELF_WALLETS: 'view self wallets',
  VIEW_SELF_WITHDRAWALS: 'view self withdrawals',
  VIEW_TEAM_CONTRACTOR_PAYMENTS: 'view team contractor_payments',

  // Other permissions
  ACTIVATE_ANY_USERS: 'activate any users',
  ADD_CUSTOM_CONTRACT_EMPLOYEES: 'add custom contract employees',
  APPROVE_ANY_WITHDRAWALS: 'approve any withdrawals',
  APPROVE_OWN_CONTRACTOR_PAYMENTS: 'approve own contractor_payments',
  APPROVE_TEAM_CONTRACTOR_PAYMENTS: 'approve team contractor_payments',
  ASSIGN_ANY_PERMISSIONS: 'assign any permissions',
  ASSIGN_ANY_ROLE: 'assign any role',
  ASSIGN_OWN_ROLE: 'assign own role',
  DECLINE_ANY_WITHDRAWALS: 'decline any withdrawals',
  FORCE_2FA_AUTH: 'force 2fa auth',
  MARK_PAID_ANY_CONTRACTOR_PAYMENTS: 'mark paid any contractor_payments',
  PURGE_ANY_USER_WALLET_DATA: 'purge any user_wallet_data',
  REJECT_OWN_CONTRACTOR_PAYMENTS: 'reject own contractor_payments',
  REJECT_TEAM_CONTRACTOR_PAYMENTS: 'reject team contractor_payments',
  REVOKE_ANY_PERMISSIONS: 'revoke any permissions',
  REVOKE_SELF_PERMISSIONS: 'revoke self permissions',
  SIGN_SELF_EMPLOYMENT_CONTRACT: 'sign self employment_contract',
  TOPUP_ANY_WALLETS: 'topup any wallets',
  TOPUP_SELF_WALLETS: 'topup self wallets',
  WITHDRAW_ANY_WALLETS: 'withdraw any wallets',
  WITHDRAW_SELF_WALLETS: 'withdraw self wallets',
};

/** Backend permission IDs for payslip subtype document permissions (eor, payroll_seat, b2c, contractor). */
export const PAYSLIP_SUBTYPE_DOCUMENT_PERMISSION_IDS = {
  [PERMISSIONS.VIEW_ANY_PAYSLIP_EOR_DOCUMENTS]: 644,
  [PERMISSIONS.VIEW_OWN_PAYSLIP_EOR_DOCUMENTS]: 645,
  [PERMISSIONS.VIEW_IN_OWN_PAYSLIP_EOR_DOCUMENTS]: 646,
  [PERMISSIONS.VIEW_SELF_PAYSLIP_EOR_DOCUMENTS]: 647,
  [PERMISSIONS.VIEW_ANY_PAYSLIP_PAYROLL_SEAT_DOCUMENTS]: 648,
  [PERMISSIONS.VIEW_OWN_PAYSLIP_PAYROLL_SEAT_DOCUMENTS]: 649,
  [PERMISSIONS.VIEW_IN_OWN_PAYSLIP_PAYROLL_SEAT_DOCUMENTS]: 650,
  [PERMISSIONS.VIEW_SELF_PAYSLIP_PAYROLL_SEAT_DOCUMENTS]: 651,
  [PERMISSIONS.CREATE_ANY_PAYSLIP_EOR_DOCUMENTS]: 652,
  [PERMISSIONS.UPDATE_ANY_PAYSLIP_EOR_DOCUMENTS]: 653,
  [PERMISSIONS.CREATE_OWN_PAYSLIP_EOR_DOCUMENTS]: 654,
  [PERMISSIONS.CREATE_SELF_PAYSLIP_EOR_DOCUMENTS]: 655,
  [PERMISSIONS.CREATE_ANY_PAYSLIP_PAYROLL_SEAT_DOCUMENTS]: 656,
  [PERMISSIONS.UPDATE_ANY_PAYSLIP_PAYROLL_SEAT_DOCUMENTS]: 657,
  [PERMISSIONS.CREATE_OWN_PAYSLIP_PAYROLL_SEAT_DOCUMENTS]: 658,
  [PERMISSIONS.CREATE_SELF_PAYSLIP_PAYROLL_SEAT_DOCUMENTS]: 659,
  [PERMISSIONS.DELETE_ANY_PAYSLIP_EOR_DOCUMENTS]: 660,
  [PERMISSIONS.DELETE_OWN_PAYSLIP_EOR_DOCUMENTS]: 661,
  [PERMISSIONS.DELETE_SELF_PAYSLIP_EOR_DOCUMENTS]: 662,
  [PERMISSIONS.DELETE_ANY_PAYSLIP_PAYROLL_SEAT_DOCUMENTS]: 663,
  [PERMISSIONS.DELETE_OWN_PAYSLIP_PAYROLL_SEAT_DOCUMENTS]: 664,
  [PERMISSIONS.DELETE_SELF_PAYSLIP_PAYROLL_SEAT_DOCUMENTS]: 665,
  [PERMISSIONS.VIEW_ANY_PAYSLIP_B2C_DOCUMENTS]: 667,
  [PERMISSIONS.VIEW_OWN_PAYSLIP_B2C_DOCUMENTS]: 668,
  [PERMISSIONS.VIEW_IN_OWN_PAYSLIP_B2C_DOCUMENTS]: 669,
  [PERMISSIONS.VIEW_SELF_PAYSLIP_B2C_DOCUMENTS]: 670,
  [PERMISSIONS.VIEW_ANY_PAYSLIP_CONTRACTOR_DOCUMENTS]: 671,
  [PERMISSIONS.VIEW_OWN_PAYSLIP_CONTRACTOR_DOCUMENTS]: 672,
  [PERMISSIONS.VIEW_IN_OWN_PAYSLIP_CONTRACTOR_DOCUMENTS]: 673,
  [PERMISSIONS.VIEW_SELF_PAYSLIP_CONTRACTOR_DOCUMENTS]: 674,
  [PERMISSIONS.CREATE_ANY_PAYSLIP_B2C_DOCUMENTS]: 675,
  [PERMISSIONS.UPDATE_ANY_PAYSLIP_B2C_DOCUMENTS]: 676,
  [PERMISSIONS.CREATE_OWN_PAYSLIP_B2C_DOCUMENTS]: 677,
  [PERMISSIONS.CREATE_SELF_PAYSLIP_B2C_DOCUMENTS]: 678,
  [PERMISSIONS.CREATE_ANY_PAYSLIP_CONTRACTOR_DOCUMENTS]: 679,
  [PERMISSIONS.UPDATE_ANY_PAYSLIP_CONTRACTOR_DOCUMENTS]: 680,
  [PERMISSIONS.CREATE_OWN_PAYSLIP_CONTRACTOR_DOCUMENTS]: 681,
  [PERMISSIONS.CREATE_SELF_PAYSLIP_CONTRACTOR_DOCUMENTS]: 682,
  [PERMISSIONS.DELETE_ANY_PAYSLIP_B2C_DOCUMENTS]: 683,
  [PERMISSIONS.DELETE_OWN_PAYSLIP_B2C_DOCUMENTS]: 684,
  [PERMISSIONS.DELETE_SELF_PAYSLIP_B2C_DOCUMENTS]: 685,
  [PERMISSIONS.DELETE_ANY_PAYSLIP_CONTRACTOR_DOCUMENTS]: 686,
  [PERMISSIONS.DELETE_OWN_PAYSLIP_CONTRACTOR_DOCUMENTS]: 687,
  [PERMISSIONS.DELETE_SELF_PAYSLIP_CONTRACTOR_DOCUMENTS]: 688,
} as const satisfies Record<string, number>;

export const MSA_VERIFICATION_TYPES = {
  MSA_DETAILS: 'msa_details',
  EMPLOYMENT_DETAILS: 'employment_details',
};

export const MSA_DETAILS_STATUS = {
  NOT_STARTED: 'not_started',
  UNDER_REVIEW: 'under_review',
  AWAITING_COMPANY_OWNER_APPROVAL: 'awaiting_the_company_owner',
  AWAITING_FINAL_SIGNATURE: 'awaiting_final_signature',
  SIGNED: 'signed',
};

export const KYB_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

export const KYC_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

export const CONTINENTS_TO_VALUE = {
  Europe: 1,
  Africa: 2,
  'North America': 3,
  'South America': 3,
  Asia: 4,
  Oceania: 5,
};

export const EMPLOYEE_TYPE = {
  LOCAL: 'local',
  EXPAT: 'expat',
};

export const EMPLOYEE_CONTRACT_TYPE = {
  FIXED: 'fixed',
  PERMANENT: 'indefinite',
};

// Backend-assigned employee job ids that the UI needs to branch on.
// Keep in sync with the `employee_jobs` table; the `OTHER` row drives the
// custom job title/description flow in the admin TMP.
export const EMPLOYEE_JOB_ID = {
  OTHER: 103,
} as const;

export const INVOICE_TYPES = {
  DEPOSIT: 'deposit',
  PAYROLL: 'payroll',
  GENERAL: 'general',
};

export const EMPLOYEE_STATUS = {
  EMPLOYED: 'employed',
  EMPLOYEE_REQUESTED_CHANGES: 'employee_requested_changes',
  AWAITING_FINAL_SIGNATURE: 'waiting_for_signature',
  UNDER_REVIEW: 'under_review',
  DRAFT: 'draft',
  TERMINATED: 'terminated',
  EMPLOYER_REQUESTED_CHANGES: 'employer_requested_changes',
};

export const PERSON_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  ONBOARDING: 'onboarding',
} as const;

export const ENTITY_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const;

export const ENTITY_OWNERSHIP_TYPE = {
  NT_OWNED: 'nt_owned',
  CLIENT_OWNED: 'client_owned',
} as const;

export const USER_TYPE = {
  EMPLOYER: 'employer',
  EMPLOYEE: 'employee',
};

export const EMPLOYEE_PAYROLL_TYPES = {
  BONUS: 'bonus',
  EXPENSE: 'expense',
  SICK_LEAVE: 'sick_leave',
  CONTRACTED_ANNUAL_LEAVE: 'contracted_annual_leave',
  UNPAID_LEAVE: 'unpaid_leave',
  SALARY_CHANGE: 'salary_change',
  OTHER: 'other',
  OVERTIME_HOURS: 'overtime_hours',
  NONE: 'none',
  CONTRACT_AMENDMENT: 'contract_amendment',
};

export const EMPLOYEE_PAYROLL_EXPENSE_TYPES = {
  PREPAID_EXPENSE: 'prepaid_expense',
  REIMBURSEMENT: 'reimbursement',
};

export const PAYROLL_STATUS = {
  ALL: 'all',
  PENDING: 'pending',
  APPROVED: 'approved',
  DECLINED: 'declined',
} as const;

export const PAYROLL_ALERT_TYPE = {
  ADD: 'add',
  EDIT: 'edit',
};

export const DOCUMENT_TYPES = {
  ANNEXE: 'annexe',
  PAYSLIP: 'payslip',
  PAYROLL: 'payroll',
  CONTRACT: 'contract',
  MSA: 'msa',
  OTHER: 'other',
  PRICING_ADDENDUM: 'pricing_addendum',
  SUPPORTING_DOCUMENTS: 'supporting_documents',
  ANNUAL_REPORT: 'annual_report',
  INVOICE: 'invoice',
};

export const INVOICE_STATUS = {
  PENDING: 'pending',
  PAID: 'paid',
  OVERDUE: 'overdue',
  DRAFT: 'draft',
  SCHEDULED: 'scheduled',
  CANCELED: 'canceled',
  PAYING: 'paying',
  ALL: 'all',
} as const;

export const PAYROLL_DIALOG_STATE = {
  ADD: 'add',
  EDIT: 'edit',
  MANAGE: 'manage',
  VIEW: 'view',
};
export const TOP_UP_METHOD_OPTIONS = {
  CARD: 'card',
  BANK: 'bank',
} as const;

export const BANK_TRANSFER_TYPE_OPTIONS = {
  SEPA: 'sepa',
  SWIFT: 'swift',
} as const;

export const BANK_ACCOUNT_TYPES = {
  IBAN: 'iban',
  ACCOUNT_NUMBER: 'account_number',
} as const;

export const ACCESSIBLE_APPS = {
  ADMIN: 'admin',
  EMPLOYEE: 'employee',
  CONTRACTOR: 'contractor',
  EMPLOYER: 'employer',
  WALLET: 'wallet',
  GIG_WORKER: 'gig_worker',
};

export const ALERT_PRIORITY = {
  MEDIUM: 'medium',
  HIGHEST: 'highest',
  CRITICAL: 'critical',
};

export const KYT_STATUS_OPTIONS = {
  SUCCESS: 'success',
  NOT_CHECKED: 'notChecked',
  HIGH_RISK: 'highRisk',
  FAIL: 'fail',
} as const;

export const PAYMENT_REQUEST_STATUS_OPTIONS = {
  NEW: 'new',
  REFUNDED: 'refunded',
  PENDING: 'pending',
  RESERVED: 'reserved',
  COMPLETED: 'completed',
  PAID: 'paid',
  DECLINED: 'declined',
  CANCELLED: 'cancelled',
  FAILED: 'failed',
} as const;

export const PAYMENT_REQUEST_UPDATE_STATUS_OPTIONS = {
  PENDING: 'pending',
  APPROVE: 'approve',
  PAY: 'pay',
  DECLINE: 'decline',
} as const;

export const PAYMENTS_TO_DO_LIST_OPTIONS = {
  WITHDRAWAL_REQUESTS: 'withdrawal-requests',
  EPS_CARD_TOP_UPS: 'eps-card-top-up-requests',
  EPS_CARD_REQUESTS: 'eps-card-requests',
  KYT_CHECKS: 'kyt-checks',
  WALLET_TOP_UP_REQUESTS: 'wallet-top-up-requests',
  USER_VERIFICATION_REQUESTS: 'user-verification-requests',
  CLIENT_INVOICING_REQUESTS: 'client-invoicing-requests',
  IBAN_REQUESTS: 'iban-requests',
  CONTRACTOR_PAYMENTS_REQUESTS: 'contractor-payments-requests',
} as const;

export const WITHDRAWAL_REQUESTS_VIEW_PERMISSIONS = [
  PERMISSIONS.VIEW_ANY_WITHDRAWALS,
  PERMISSIONS.VIEW_ANY_TRANSACTIONS,
] as const;

export const TRANSACTION_REQUEST_TYPES = {
  WITHDRAWAL: 'outbound',
  TOP_UP: 'inbound',
} as const;

export const TOP_UP_PROVIDER_OPTIONS = {
  BANK: 'bank',
  CARD: 'card',
} as const;

export const EPS_CARD_REQUEST_STATUS_OPTIONS = {
  NEW: 'new',
  PENDING: 'pending',
  ACCEPTING: 'accepting',
  ACCEPTED: 'accepted',
  SENT: 'sent',
  ACTIVE: 'active',
  DECLINED: 'declined',
  FAILED: 'failed',
} as const;

export const DYNAMIC_FIELD_TYPES = {
  SELECT: 'select',
  DATE: 'date',
  TEXT: 'text',
} as const;

export type DynamicFieldType = (typeof DYNAMIC_FIELD_TYPES)[keyof typeof DYNAMIC_FIELD_TYPES];

/** Named writing-system presets for country text fields (charset + punctuation rules). */
/**
 * Today `georgian_mkhedruli` uses strict legal-name mode (Mkhedruli + space + hyphen).
 * If we later need the full alphabet while still rejecting other scripts, migrate to
 * `format?: { script: CountryFieldScript; strict?: boolean }` where `strict: false`
 * allows punctuation within the script. Migrate existing `script` usages when that lands.
 */
export const COUNTRY_FIELD_SCRIPTS = {
  GEORGIAN_MKHEDRULI: 'georgian_mkhedruli',
} as const;

export type CountryFieldScript = (typeof COUNTRY_FIELD_SCRIPTS)[keyof typeof COUNTRY_FIELD_SCRIPTS];

export const DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS = {
  EQUALS: 'equals',
  IN: 'in',
} as const;

export type DynamicConditionalRequiredCondition =
  (typeof DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS)[keyof typeof DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS];

export const DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS = {
  LESS_THAN: 'less_than',
  EQUALS: 'equals',
  IN: 'in',
} as const;

export type DynamicConditionalDependsCondition =
  (typeof DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS)[keyof typeof DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS];

export const DYNAMIC_FIELD_CALCULATION_TYPES = {
  ADD_YEARS_SUBTRACT_DAYS: 'add_years_subtract_days',
} as const;

export type DynamicCalculationType =
  (typeof DYNAMIC_FIELD_CALCULATION_TYPES)[keyof typeof DYNAMIC_FIELD_CALCULATION_TYPES];

/** Sentinel value for `date_range_transform.to_field` — the field uses its own value as the range end. */
export const DATE_RANGE_TRANSFORM_SELF = 'self' as const;

export const DATE_RANGE_TRANSFORM_UNITS = {
  DAYS: 'days',
  MONTHS: 'months',
  YEARS: 'years',
} as const;

export type DateRangeTransformUnit =
  (typeof DATE_RANGE_TRANSFORM_UNITS)[keyof typeof DATE_RANGE_TRANSFORM_UNITS];

export const API_RESPONSE_TYPES = {
  GENERAL_SUCCESS: 'general_success',
} as const;

export const USER_VERIFICATION_STATUS_OPTIONS = {
  NEW: 'initial',
  VERIFIED: 'approved',
  REJECTED: 'rejected',
  FINAL_REJECTED: 'final_rejected',
} as const;

export const USER_VERIFICATION_SUM_SUB_STATUS_OPTIONS = {
  NEW: 'init',
  PENDING: 'pending',
  ON_HOLD: 'on_hold',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  FINAL_REJECTED: 'final_rejected',
} as const;

export const WALLET_TRANSACTION_TYPE_OPTIONS = [
  { value: 'inbound', name: 'Inbound' },
  { value: 'outbound', name: 'Outbound' },
  { value: 'internal', name: 'Internal' },
];

export const WALLET_STATUS_OPTIONS = [
  { value: 'completed', name: 'Completed' },
  { value: 'pending', name: 'Pending' },
  { value: 'declined', name: 'Declined' },
  { value: 'refunded', name: 'Refunded' },
  { value: 'reserved', name: 'Reserved' },
  { value: 'cancelled', name: 'Cancelled' },
];

export const DEPOSITS_TRANSACTION_TYPE_OPTIONS = [
  { value: 'pre-payments', name: 'Deposits' },
  { value: 'rewards', name: 'Rewards' },
];

export const ACTION_TYPES = {
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CONFIRM_REJECT: 'confirm_reject',
  CONFIRM_APPROVE: 'confirm_approve',
  // This is the status which the BE accepts for rejected status
  DECLINED: 'declined',
} as const;

export const CTA_TYPES = {
  TEAM: 'team',
  INVOICES: 'invoices',
  PAYROLL: 'payroll',
  SETTINGS: 'settings',
  BANK_INFO: 'bank-info',
  ABSENCE: 'absence',
  TEAM_MEMBER_PROFILE: 'team_member_profile',
};

export const TASK_CATEGORIES = {
  COMPANY: 'company',
  TEAM_MEMBER: 'team_member',
  INVOICE: 'invoice',
};

export const TASK_TYPES = {
  CONTRACT_SIGNING: 'contract_signing',
  CONTRACT_CHANGES: 'contract_changes',
};

export const TASK_STATUSES = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  OVERDUE: 'overdue',
};

export const DEFAULT_PAGE_INDEX = 1;
export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export const KYT_CHECKS_FILTER_TYPE_OPTIONS = {
  CAUSER_IP: 'causer_ip',
  CHECKUP_ID: 'checkup_id',
  DATE: 'date',
  USER: 'user',
} as const;

export const TEAM_ROLE_IDS = {
  OWNER: 'employer',
  GLOBAL_ADMIN: 'teams_global_admin',
  TEAM_LEAD: 'teams_team_lead',
  TEAM_MEMBER: 'teams_team_member',
} as const;

export const EMPLOYMENT_TYPE_OPTIONS = {
  EMPLOYEE: 'employee',
  INDIVIDUAL_CONTRACTOR: 'individual_contractor',
  COMPANY_CONTRACTOR: 'company_contractor',
  GIG_WORKER: 'gig_worker',
  OTHER: 'other',
} as const;

export const ADMIN_EMPLOYMENT_TYPE_OPTIONS = {
  EMPLOYEE: 'employee',
  INDIVIDUAL_CONTRACTOR: 'individual_contractor',
  COMPANY_CONTRACTOR: 'company_contractor',
  GIG_WORKER: 'gig_worker',
} as const;

export const SALARY_TYPE_OPTIONS = {
  NET: 'net',
  GROSS: 'gross',
} as const;

export const SALARY_FREQUENCY_OPTIONS = {
  MONTHLY: 'monthly',
  ANNUAL: 'annual',
} as const;

export const TEAM_MEMBER_TYPE = {
  EMPLOYEE: 'employee',
  CONTRACTOR: 'contractor',
  GIG_WORKER: 'gig_worker',
  OTHER: 'other',
} as const;

export const TEAM_ROLES = {
  TEAM_LEAD: 'teams_team_lead',
  TEAM_MEMBER: 'teams_team_member',
};

export const ABSENCE_TYPES = {
  CONTRACTED_ANNUAL_LEAVE: 'contracted_annual_leave',
  SICK_LEAVE: 'sick_leave',
  UNPAID_LEAVE: 'unpaid_leave',
} as const;

export type AbsenceType = (typeof ABSENCE_TYPES)[keyof typeof ABSENCE_TYPES];

export const NOTIFICATION_CATEGORIES = {
  PAYROLL: 'payroll',
  EXPENSES: 'expenses',
  INVOICES: 'invoices',
  SETTINGS: 'settings',
  TASK: 'task',
  ORGANISATION: 'organisation',
  TEAM_MEMBERS: 'team_members',
  TEAMS: 'teams',
  MSA: 'msa',
  KYB: 'kyb',
  ABSENCE: 'absence',
  PAYSLIP: 'payslip',
  DOCUMENTS: 'documents',
} as const;

export const NOTIFICATION_SUBCATEGORIES = {
  MONTHLY_INPUTS: 'monthly_inputs',
  PAYROLL_TO_RUN: 'payroll_to_run',
  EOR_PAYROLL: 'eor_payroll',
  PAAS: 'paas',
} as const;

export const WALLET_TYPES = {
  DEFAULT: 'default',
  PRE_PAYMENT: 'pre_payment',
  ORGANISATION: 'organisation',
  ENTITY: 'entity',
  SALARY: 'salary',
};
export const ENTITY_ACTIVITY_TYPES = {
  TEAM_MEMBERS: 'team_members',
  PAYROLL: 'payroll',
  BASIC: 'basic',
} as const;

export const PAYSLIP_FILENAME_REGEX = /^\d+_(0[1-9]|1[0-2])_\d{4}_.+\.pdf$/i;
export const ANNUAL_REPORT_FILENAME_REGEX = /^\d+_ANNUAL_\d{4}_.+\.pdf$/i;
